<div class="flex flex-col gap-y-20" [formGroup]="store.fundingForm">
  <!-- Two columns: HOW MUCH on the left, HOW IT TRAVELS on the right.

       They are genuinely independent decisions -- neither answer constrains the other -- so
       stacking them made the step scroll for no reason on a modal that is already 132rem wide.
       Side by side, the whole instruction is legible at once.

       `grid` rather than two flexed halves, for the divider: grid items stretch to the row
       height, so the rule on the right column spans the full height of the taller side. Two
       flex children would each be their own height and the rule would stop short.

       Collapses to one column below `md`, where the rule becomes the horizontal separator this
       step had before -- see the right column's border classes. -->
  <div class="grid grid-cols-1 md:grid-cols-2">
    <!-- ============ Amount ============ -->
    <!-- `pr-30` pairs with the right column's `pl-30` so the divider sits centred in a 60px
         gutter rather than hugging one side. -->
    <section class="flex flex-col gap-y-5 md:pr-30">
      <h3 class="text-lg font-bold text-gray-darkest">{{ heading() }}</h3>

      @if (store.isDistribution()) {
        <!-- Close Account leads the card rather than sitting at the bottom, because ticking it
             changes what the rest of the step asks for -- and what the whole wizard contains. A
             checkbox that silently removes three steps belongs where it is read first. -->
        <div class="flex items-start px-10 py-10 mb-5 border rounded gap-x-10"
          [class.border-gray-lighter]="!store.isCloseAccount()"
          [class.bg-gray-lightest]="!store.isCloseAccount()"
          [class.border-red-lighter]="store.isCloseAccount()"
          [class.bg-red-lightest]="store.isCloseAccount()">
          <div class="flex flex-col gap-y-8">
            <label class="flex items-center text-sm font-semibold cursor-pointer gap-x-8 text-gray-darkest">
              <input type="checkbox" formControlName="isCloseAccount" />
              Close this account
            </label>
            <span class="text-xs text-gray">
              Every position is liquidated and the full account value is distributed. The models,
              positions and cash steps no longer apply.
            </span>

            @if (store.isCloseAccount()) {
              <!-- Revealed only when closing, because it is meaningless otherwise. Defaults to
                   CHECKED, matching the v1 close-account workflow's `billAccountOnClosing`, so
                   moving to V2 does not silently reverse the answer for anyone. -->
              <label class="flex items-center pt-8 mt-2 text-sm cursor-pointer gap-x-8 text-gray-darkest border-t border-red-lighter">
                <input type="checkbox" formControlName="isCloseDateBilled" />
                Bill the account on the close date
              </label>
            }
          </div>
        </div>
      }

      @if (store.isCloseAccount()) {
        <!-- No amount input: the amount IS the account. Shown read-only so the advisor can see the
             figure the request will carry, captioned as an estimate because it moves with the
             market between now and settlement. -->
        <div class="flex flex-col gap-y-2">
          <span class="text-xs uppercase text-gray">Amount to be distributed</span>
          <span class="text-xl font-bold text-gray-darkest" style="font-variant-numeric: tabular-nums">
            {{ store.fundingAmount() | currency }}
          </span>
          <span class="text-xs text-gray">
            The full account value as at load. The final figure follows the liquidation.
          </span>
        </div>
      } @else {
        @if (store.isDistribution()) {
          <!-- GROSS / NET. A convenience only: the request stores one amount, and it is always
               what LEAVES the account. See `fundingAmount` in the store. -->
          <div class="flex flex-col gap-y-5">
            <span class="text-xs uppercase text-gray">
              Would you like to enter the GROSS or NET amount?
            </span>
            <!-- 🔴 `is-mint` ON BOTH, ALWAYS, and only `is-outlined` toggles. The modifiers are
                 modifiers OF A COLOUR, not standalone looks: every rule for `is-outlined` and
                 `is-inverted` in styles.input.scss is nested under `.is-primary` or `.is-mint`.
                 Moving the colour to whichever half was selected left the other matching
                 nothing -- it fell through to the bare `.button` base and rendered as plain grey
                 text beside a filled chip, so the pair read as one button and a label rather
                 than a choice between two (AndyP, 2026-11-03).

                 ⚠️ `is-inverted` is gone rather than kept alongside. It also wins over
                 `is-outlined` -- same specificity, declared later in the file -- and its whole
                 job is removing the border, which is the one thing this needed. V1's toggle
                 carries the same `is-mint is-inverted is-outlined` trio and looks borderless for
                 exactly that reason. -->
            <div class="flex gap-x-2">
              <button type="button" class="button is-sm is-mint"
                [class.is-outlined]="store.isNetEntry()"
                [attr.aria-pressed]="!store.isNetEntry()"
                (click)="setEntryMode('gross')">GROSS</button>
              <button type="button" class="button is-sm is-mint"
                [class.is-outlined]="!store.isNetEntry()"
                [attr.aria-pressed]="store.isNetEntry()"
                (click)="setEntryMode('net')">NET</button>
            </div>
          </div>
        }

        <!-- ⚠️ MARKED WHILE EMPTY, not after being touched (AndyP, 2026-09-22). The usual rule
             is to stay quiet until the advisor has engaged with a field, and it is wrong here:
             this step opens with two empty required fields and a disabled Next whose single
             message names only the first of them, so the advisor fixes the amount, presses Next,
             and only then learns about the bank account. Marking both from the start is what
             makes the step's shape visible.

             🟢 Not an ERROR state. It says "still needed", which is why it clears the moment a
             figure is entered rather than waiting for a blur, and why an amount that is merely
             too large is not marked here -- that has its own message. -->
        <label class="mt-10 text-xs uppercase text-gray" for="funding-amount">{{ amountLabel() }}</label>
        <div class="flex items-center gap-x-5">
          <span class="text-gray-darkest">$</span>
          <input id="funding-amount" type="number" min="0" formControlName="amount"
            appSelectOnFocus appCommitOnEnter appTwoDecimals
            class="w-200 text-right input no-spinner"
            [class.border-red-dark]="store.fundingAmountMissing()"
            [attr.aria-invalid]="store.fundingAmountMissing() ? 'true' : null"
            [attr.aria-describedby]="store.fundingAmountMissing() ? 'funding-amount-required' : null"
            [attr.aria-label]="amountLabel()"
            (change)="onAmount($event)" />
          @if (store.fundingAmountMissing()) {
            <span id="funding-amount-required" class="text-xs text-red-dark">Required</span>
          }
        </div>

      }

      <!-- ⚠️ OUTSIDE the close/amount branch above, deliberately. This used to sit inside the
           @else, so ticking Close Account skipped the withholding fields along with the amount
           input — and a close is exactly when withholding matters most, since the whole account
           is going out (AndyP, 2026-09-23).

           🟢 The STORE always handled it: `federalTaxPct` gates on `isDistribution()` alone, and
           `netAmount` derives from `fundingAmount`, which on a close is the account total. Only
           the inputs were missing, so nothing about the arithmetic changes here. -->
      @if (store.isDistribution()) {
        <!-- ============ Withholding ============ -->
        <div class="flex flex-wrap mt-15 gap-x-30 gap-y-10">
          <div class="flex flex-col gap-y-2">
            <span class="text-xs uppercase text-gray">Federal Tax</span>
            <span class="text-xs text-gray">
              CURRENT: {{ currentTaxCaption(store.funding()?.federalTaxWithholding) }}
            </span>
            <div class="flex items-center gap-x-5">
              <input type="number" min="0" max="100" formControlName="federalTaxPct"
                appSelectOnFocus appCommitOnEnter appTwoDecimals
                class="w-120 text-right input is-sm no-spinner" placeholder="0.00"
                aria-label="Federal tax withholding percent"
                (change)="onFederalTax($event)" />
              <span class="text-gray-darkest">%</span>
            </div>
          </div>
          <div class="flex flex-col gap-y-2">
            <span class="text-xs uppercase text-gray">State Tax</span>
            <span class="text-xs text-gray">
              CURRENT: {{ currentTaxCaption(store.funding()?.stateTaxWithholding) }}
            </span>
            <div class="flex items-center gap-x-5">
              <input type="number" min="0" max="100" formControlName="stateTaxPct"
                appSelectOnFocus appCommitOnEnter appTwoDecimals
                class="w-120 text-right input is-sm no-spinner" placeholder="0.00"
                aria-label="State tax withholding percent"
                (change)="onStateTax($event)" />
              <span class="text-gray-darkest">%</span>
            </div>
          </div>
        </div>

        @if (store.taxWithholdingChanged()) {
          <!-- The V1 wording, kept verbatim: it is a standing consequence the advisor has to be
               told about, not a validation message. Non-blocking, hence orange. -->
          <div class="flex items-start px-10 py-8 mt-10 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              <strong>Please Note</strong>, if you are changing withholding elections, they will
              remain in effect on all distributions for this account until you change or revoke it.
            </span>
          </div>
        }

        @if (store.fundingAmount() > 0) {
          <div class="px-15 py-10 mt-10 text-sm border rounded border-mint-lighter bg-mint-lightest">
              <!-- ⚠️ SUPPRESSED ON A CLOSE, where the amount is already on screen
                   read-only a few lines up. A figure printed twice on one screen invites
                   the reader to check whether the two agree. The NET line below is the
                   part a close still needs, and it is the reason this panel follows the
                   tax fields out of the else. -->
              @if (!store.isCloseAccount()) {
                <p class="text-gray-darkest">
                  <span class="font-bold text-mint-darker">{{ store.fundingAmount() | currency }}</span>
                  will be distributed from this account.
                </p>
              }
            <p class="mt-5 text-gray-darkest">
              Your client will receive
              <span class="font-bold text-mint-darker">{{ store.netAmount() | currency }}</span>
              after applicable tax withholdings.
            </p>
          </div>
        }
      }
    </section>

    <!-- ============ Delivery ============ -->
    <!-- One border, two orientations. Stacked (below md) it is the horizontal rule above this
         section, with the padding that goes with it; side by side it becomes the vertical
         divider between the columns and that padding is cancelled. -->
    <section class="flex flex-col pt-20 mt-20 border-t gap-y-15 border-gray-lightest
      md:pt-0 md:mt-0 md:border-t-0 md:border-l md:pl-30">
      <h3 class="text-lg font-bold text-gray-darkest">Delivery</h3>

      <!-- No "options could not be loaded" branch here on purpose: an empty or failed lookup is
           FATAL for these request types (DeliveryOptionsUnavailableError), so the wizard never
           reaches this step with an empty list — it shows the full-height load-error panel
           instead. A warning here would describe a state that cannot occur. -->
      <div class="flex flex-col gap-y-5">
        <!-- The label carries the info icon and the reason when the method is fixed, which is V1's
             treatment: a greyed select sitting on ACH with the explanation on hover, rather than a
             one-item dropdown that reads as a choice the advisor is failing to make. -->
        <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="delivery-method">
          Please choose a {{ requestNoun() }} method
          @if (store.deliveryMethodLocked()) {
            <i class="material-icons text-mint-dark" style="font-size: 1.4rem; cursor: help"
              [title]="lockedMethodReason()">info</i>
          }
        </label>
        <!-- A native `disabled`, not V1's pointer-events/opacity pair. This select is bound with
             (change) rather than formControlName, so disabling the ELEMENT leaves the form's value
             intact and submittable -- which is the whole reason V1 reached for the CSS trick. A
             real disabled attribute is also reachable by assistive tech, where
             pointer-events: none simply makes the control silently inert.

             🔴 `[selected]` on the OPTIONS, never `[value]` on the select. A native select's
             `value` property is applied before its `@for` options exist, so the browser has
             nothing to match and falls back to showing the FIRST option. That read as "Select a
             method" while ACH was in fact selected in the store -- and made the gate look absent,
             because Next was correctly enabled against a method the advisor could not see. Same
             pattern the model-allocation dropdown uses.

             🔴 A DISTRIBUTION OPENS ON THE PLACEHOLDER (AndyP, 2026-11-03), and the methods
             are not interchangeable: a cheque is posted, an ACH lands in a bank account, Leave
             in Cash never leaves the custodian. A pre-selected first entry was the form
             answering on the advisor's behalf, and alphabetical order is no reason to.

             It reads as nothing chosen because nothing IS -- the store stops seeding unless the
             select is locked -- so this now matches the two selects further down rather than
             standing apart from them. A contribution still opens on its one method, greyed,
             with the reason on hover.

             ⚠️ The placeholder is rendered for EVERY request type, including the locked one.
             It is never the selection there (the store seeds the only method), and omitting it
             conditionally would make the option list depend on the lock as well as the
             selection -- two reasons for one element to appear. -->
        <select id="delivery-method" class="select w-320"
          [class.border-red-dark]="store.deliveryMethodMissing()"
          [attr.aria-invalid]="store.deliveryMethodMissing() ? 'true' : null"
          [disabled]="store.deliveryMethodLocked()"
          (change)="onMethod($event)">
          <option value="" [selected]="store.selectedDeliveryMethod() === null">
            Select {{ requestNoun() }} method
          </option>
          @for (method of store.deliveryMethods(); track method.id) {
            <option [value]="method.id" [selected]="method.id === store.selectedDeliveryMethod()?.id">
              {{ method.name }}
            </option>
          }
        </select>
        @if (store.deliveryMethodLocked()) {
          <span class="text-xs text-gray">{{ lockedMethodReason() }}</span>
        }
      </div>

      <!-- ============ Where a cheque is posted ============
           Check alone, by `req.DeliveryMethod.UsesAddressBook`. Every other method either lands
           in a bank account, keeps the money inside the custodian, or names its destination in
           the method itself.

           🔴 THESE ARE SCHWAB'S OWN type-C STANDING INSTRUCTIONS -- the same list the bank select
           below reads, filtered by the method's code. A type-C counterparty is a mailing address
           with no account number in it, and our API discarded exactly those rows until
           2026-11-02, which is why a cheque looked like an account with no instructions at all.

           /!\ Shares `standingInstructionKey` with the bank select. Only one of the two is ever
           on screen, both answer WHERE DOES THIS LAND, and the chosen row's displayValue is what
           reaches DeliveryInstruction either way. A second control would be a second thing to
           clear on every method change. -->
      @if (store.usesAddressBook()) {
        <div class="flex flex-col gap-y-5">
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="delivery-address">
            Where is the {{ requestNoun() }} being sent?
            @if (store.addressMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <!-- Keeps its placeholder, like the bank select and unlike the method select above:
               this decides where a client's money physically goes, and picking one for the
               advisor would be making that decision for them.

               ⚠️ The ONE exception is an account with nothing on file, where the store
               pre-selects New (Other Address) and opens the payee panel. That is not a default
               being chosen -- it is the only row there is, and making someone pick from a list of
               one is the forced non-answer this card exists to remove. -->
          <select id="delivery-address" class="select w-320"
            [class.border-red-dark]="store.addressMissing()"
            [attr.aria-invalid]="store.addressMissing() ? 'true' : null"
            (change)="onAddress($event)">
            <option value="" [selected]="store.selectedStandingInstruction() === null">
              Select address
            </option>
            @for (address of store.addressOptions(); track address.key) {
              <option [value]="address.key"
                [selected]="address.key === store.selectedStandingInstruction()?.key">
                {{ address.displayValue }}
              </option>
            }
          </select>
        </div>

        <!-- THE CHOSEN ADDRESS, READ BACK. Schwab files it as three free-text lines with no
             city/state/zip breakdown, so it is rendered whole and never parsed -- splitting it
             would be guesswork shown to an advisor as fact.

             ⚠️ Read-only, and NOT copied into the payee fields. It already lives at the
             custodian; a duplicate in CRM could only go stale, and the request files the row's
             label so the trade team can say which address was meant. -->
        @if (store.selectedAddressLines().length > 0) {
          <div class="flex flex-col px-15 py-10 border rounded gap-y-2 border-gray-lighter bg-gray-lightest">
            <span class="text-xs uppercase text-gray">Address on file</span>
            @for (line of store.selectedAddressLines(); track $index) {
              <span class="text-sm text-gray-darkest">{{ line }}</span>
            }
          </div>
        }

        @if (store.addressBookEmpty()) {
          <!-- A STATEMENT, NOT A WARNING, and deliberately not the custodial-paperwork sentence
               the bank list shows. Typing an address is a complete, ordinary answer for a cheque,
               so there is nothing here for the advisor to go and fix -- and sending them to
               Schwab to file paperwork would be sending them nowhere. -->
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-gray-lighter bg-gray-lightest text-gray-dark">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>{{ noAddressesNote }}</span>
          </div>
        }
      }

      @if (store.selectedDeliveryMethod() && store.usesBankAccount()) {
        <div class="flex flex-col gap-y-5">
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="standing-instruction">
            Bank account
            <!-- ⚠️ On the LABEL here, where the amount carries it beside the input. The select is
                 320px of mostly empty space with its placeholder on the left, so a hint at its
                 right edge reads as unrelated to the words it qualifies. -->
            @if (store.bankAccountMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <!-- Keeps its placeholder, unlike the two selects around it: there is no sensible
               default bank account, and picking one for the advisor would be choosing where a
               client's money goes.

               ⚠️ Which is exactly why it is marked: the other two selects open on a usable
               default, so this is the only one on the panel that is genuinely outstanding, and
               nothing about a placeholder distinguishes "not chosen" from "chosen and shown". -->
          <select id="standing-instruction" class="select w-320"
            [class.border-red-dark]="store.bankAccountMissing()"
            [attr.aria-invalid]="store.bankAccountMissing() ? 'true' : null"
            (change)="onStandingInstruction($event)">
            <option value="" [selected]="store.selectedStandingInstruction() === null">
              Select bank account
            </option>
            @for (instruction of store.bankAccountOptions(); track instruction.key) {
              <option [value]="instruction.key"
                [selected]="instruction.key === store.selectedStandingInstruction()?.key">
                {{ instruction.displayValue }}
              </option>
            }
          </select>
        </div>
      }

      <!-- ============ Who the money is going to ============
           🔴 DIRECTLY UNDER THE DESTINATION SELECT, above that select's panels (AndyP,
           2026-11-03). It sat below the whole bank block, so a wire asked it after two orange
           notices about custodial paperwork -- the question arriving after the consequences of
           an answer it is a part of.

           ⚠️ WHICH METHODS ASK IS A COLUMN, `AsksRecipientType`. Check and Wire today; the next
           one is a row rather than a release. Deliberately NOT derived from
           `newRecipientRequiresPayee`: a wire asks and collects no address, a cheque does both,
           and tying a declaration to a postal address only a cheque has is the conflation that
           moved this question four times in two days.

           ⚠️ Only alongside the New/Other row. A destination the custodian already holds asks
           nothing: they have established whose it is.

           🔴 NO DEFAULT, and no sorting either. Neither radio is pre-selected -- a pre-ticked
           First Party would be the form declaring on the advisor's behalf that money is not
           leaving the family, which is the one thing this question exists to establish. -->
      @if (store.recipientTypeApplies()) {
        <div class="flex flex-col gap-y-5">
          <!-- Sentence case like every label on this step; the uppercase is the CSS's. The NOUN
               follows the control above it -- an account for a bank destination, an address for
               a posted one -- because 'Address type' over a bank account reads as a mistake. -->
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray">
            {{ recipientTypeLabel() }}
            @if (store.recipientTypeMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <div class="flex gap-x-20"
            [class.p-5]="store.recipientTypeMissing()"
            [class.border]="store.recipientTypeMissing()"
            [class.rounded]="store.recipientTypeMissing()"
            [class.border-red-dark]="store.recipientTypeMissing()">
            @for (type of store.deliveryRecipientTypes(); track type.id) {
              <label class="flex items-center text-sm cursor-pointer gap-x-8 text-gray-dark">
                <input type="radio" [value]="type.id" name="delivery-recipient-type"
                  [checked]="type.id === store.selectedRecipientType()?.id"
                  [attr.aria-invalid]="store.recipientTypeMissing() ? 'true' : null"
                  (change)="onRecipientType(type.id)" />
                {{ type.name }}
              </label>
            }
          </div>
        </div>
      }

      @if (store.selectedDeliveryMethod() && store.usesBankAccount()) {
        @if (store.funding()?.standingInstructionsFailed) {
          <!-- 🔴 Distinct from "none on file", and deliberately so. A failed lookup that rendered
               as an empty list would tell the advisor to file custodial paperwork for
               authorizations that may well already exist. -->
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              This account's bank authorizations could not be loaded, so they are not shown. Close
              the request and try again rather than assuming none are on file.
            </span>
          </div>
        } @else if (showNoInstructionsWarning() || showNoAuthorityWarning()) {
          <!-- The V1 copy, verbatim, including the Schwab link -- the advisor's next action is
               unchanged and this is the wording they already know. -->
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              <strong>No Standing Authorization on account for this selection.</strong><br />
              In order to establish a new transfer/payment, please submit the necessary custodial
              paperwork.<br />
              <a class="underline" href="api/sso/schwab?destination=D_DASH" target="_blank"
                rel="noopener" aria-label="Schwab Advisor Center">Go to Schwab Advisor Center</a>
            </span>
          </div>
        }

        <!-- WHAT TO DO ABOUT IT, directly beneath the choice that caused it. The panel above
             states what is true of the account; this one states what the advisor must now do, so
             the pair reads cause then action.

             /!\ IT LIVED ABOVE THE SELECT until 2026-11-02, where it described a consequence of
             an answer the advisor had not yet given -- the screenshot that moved it shows the
             instruction sitting above an untouched Bank account dropdown.

             The sentence NAMES THE METHOD (store.newRecipientInstructionNote), so it reads
             correctly for whichever method carries the requirement: today a wire, and ACH or
             Journal Cash the moment a row is edited.

             Shown attached or not, and deliberately never turning green: this step sits upstream
             of the Documents step, so a panel here that changed state from a later screen would
             read as a progress display rather than as something to go and do. -->
        @if (store.newRecipientDocumentApplies()) {
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>{{ store.newRecipientInstructionNote() }}</span>
          </div>
        }
      }

      <!-- ============ What picking New/Other obliges ============
           Both hang off the SAME fact: the advisor chose the synthetic row, so nothing is on file
           at the custodian. Which obligation follows is the method's to say --
           NewRecipientRequiresDocument, NewRecipientRequiresPayee -- and the two never both apply.

           /!\ ONLY THE PAYEE PANEL IS HERE. The document panel travels with the BANK ACCOUNT
           select above (AndyP, 2026-11-02), because that is the control whose answer caused it --
           it was above the select, describing a consequence of a choice the advisor had not yet
           scrolled to.

           /!\ BOTH DESTINATION CONTROLS ARE NOW ABOVE THIS, and that is why the bank block moved
           up on 2026-11-03. The address select always was; the bank select was not, so a WIRE
           read ADDRESS TYPE before BANK ACCOUNT -- the declaration before the destination it is
           about. Only one of the two ever renders, so putting them together costs a cheque
           nothing and fixes the order for both. -->
      @if (store.isNewRecipient()) {
          <!-- ============ Who the money is going to ============
               🔴 OUTSIDE THE PAYEE PANEL, and that is the whole shape of this. It sat inside for
               one day, which worked only while Check was the only method asking. A WIRE asks the
               same question and collects no address at all -- its destination is a bank account
               -- so a control nested in the payee block was unreachable for half the methods
               that need it.

               ⚠️ WHICH METHODS ASK IS A COLUMN, `AsksRecipientType`. Check and Wire today; the
               next one is a row rather than a release. It is deliberately NOT derived from
               `newRecipientRequiresPayee`, because a wire asks and collects nothing -- tying the
               declaration to a postal address is the conflation that moved this question four
               times in two days.

               ⚠️ Only alongside the New/Other row. Picking a destination the custodian already
               holds asks nothing: they have established whose it is.

               🔴 NO DEFAULT, and no sorting either. The rows arrive in the lookup's own order
               and neither is pre-selected -- a pre-ticked First Party would be the form
               declaring on the advisor's behalf that money is not leaving the family, which is
               the one thing this question exists to establish.

               Radios rather than a select: two options, both short, and the choice is worth
               reading at a glance rather than opening. -->
          @if (store.payeeRequired()) {
            <!-- A CHEQUE wants an address, typed here, because somebody has to post it. The two
                 obligations ask for exactly one thing each -- neither is a weaker version of the
                 other, and a wire fills none of this. -->
            <div class="flex flex-col p-15 border rounded gap-y-10 border-gray-lighter bg-gray-lightest">
              <div class="flex items-start text-xs gap-x-8 text-orange-darker">
                <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
                <span>{{ payeeNote }}</span>
              </div>

              <div class="flex flex-col gap-y-10" [formGroup]="store.fundingForm.controls.payee">
                <div class="flex flex-col gap-y-5">
                  <!-- Marked the same way the Bank account label is: these are required whenever
                       they are rendered at all, and an empty box looks no different from an
                       optional one. -->
                  <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="payee-name">
                    Payee name
                    @if (payeeMissing('payeeName')) {
                      <span class="normal-case text-red-dark">Required</span>
                    }
                  </label>
                  <input id="payee-name" type="text" class="input" maxlength="200"
                    formControlName="payeeName" autocomplete="off"
                    [class.border-red-dark]="payeeMissing('payeeName')"
                    [attr.aria-invalid]="payeeMissing('payeeName') ? 'true' : null" />
                </div>

                <div class="flex flex-col gap-y-5">
                  <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="payee-street">
                    Street address
                    @if (payeeMissing('streetAddress')) {
                      <span class="normal-case text-red-dark">Required</span>
                    }
                  </label>
                  <input id="payee-street" type="text" class="input" maxlength="200"
                    formControlName="streetAddress" autocomplete="off"
                    [class.border-red-dark]="payeeMissing('streetAddress')"
                    [attr.aria-invalid]="payeeMissing('streetAddress') ? 'true' : null" />
                </div>

                <div class="flex flex-col gap-y-5">
                  <!-- The ONLY optional field, and it carries no marker for that reason. Most
                       addresses have no apartment line. -->
                  <label class="text-xs uppercase text-gray" for="payee-street-2">
                    Address line 2 <span class="normal-case">(optional)</span>
                  </label>
                  <input id="payee-street-2" type="text" class="input" maxlength="200"
                    formControlName="addressContinued" autocomplete="off" />
                </div>

                <div class="flex flex-col gap-y-5">
                  <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="payee-city">
                    City
                    @if (payeeMissing('city')) {
                      <span class="normal-case text-red-dark">Required</span>
                    }
                  </label>
                  <input id="payee-city" type="text" class="input" maxlength="100"
                    formControlName="city" autocomplete="off"
                    [class.border-red-dark]="payeeMissing('city')"
                    [attr.aria-invalid]="payeeMissing('city') ? 'true' : null" />
                </div>

                <div class="flex flex-wrap gap-x-15 gap-y-10">
                  <div class="flex flex-col gap-y-5">
                    <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="payee-state">
                      State
                      @if (payeeMissing('state')) {
                        <span class="normal-case text-red-dark">Required</span>
                      }
                    </label>
                    <!-- A select rather than two typed characters: the code is what is stored and
                         what the trade team re-keys, and 'Washington' typed into a two-character
                         box is a silently wrong address. 50 states plus DC; a payee anywhere else
                         is rare enough to be worth a conversation. -->
                    <select id="payee-state" class="select" style="width: 20rem"
                      formControlName="state"
                      [class.border-red-dark]="payeeMissing('state')"
                      [attr.aria-invalid]="payeeMissing('state') ? 'true' : null">
                      <option value="">Select state</option>
                      @for (state of states; track state.code) {
                        <option [value]="state.code">{{ state.name }}</option>
                      }
                    </select>
                  </div>

                  <div class="flex flex-col gap-y-5">
                    <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="payee-zip">
                      ZIP
                      @if (payeeMissing('zip')) {
                        <span class="normal-case text-red-dark">Required</span>
                      }
                    </label>
                    <!-- `inputmode` rather than `type="number"`: a ZIP is a string of digits, not
                         a quantity, and a number input would strip a leading zero -- which is
                         every address in New England. -->
                    <input id="payee-zip" type="text" class="input" style="width: 12rem"
                      inputmode="numeric" maxlength="10" formControlName="zip" autocomplete="off"
                      placeholder="12345"
                      [class.border-red-dark]="payeeMissing('zip')"
                      [attr.aria-invalid]="payeeMissing('zip') ? 'true' : null" />
                  </div>
                </div>
              </div>
            </div>
          }
      }


      <!-- Only a cheque is physically posted, so only a cheque can be sent overnight. Read off
           `req.DeliveryMethod.AllowsOvernightDelivery` rather than from Schwab's transaction-type
           code, which is where this used to get it -- the question is about the method, not about
           Schwab's taxonomy, and a relabelled row used to switch it off silently.
           ⚠️ Reachable on distributions only in practice, since a contribution is locked to ACH —
           but written against the method rather than the request type, because that is what the
           question is actually about. -->
      @if (store.allowsOvernightDelivery()) {
        <label class="flex items-center text-sm cursor-pointer gap-x-8 text-gray-darkest">
          <input type="checkbox" formControlName="isOvernightDelivery" />
          Send the check overnight
        </label>
      }

      <div class="flex flex-col gap-y-5">
        <!-- Carries the info icon when the frequency is fixed, exactly as the delivery method
             above does. Two locked selects on one step should not look like two different
             mechanisms. -->
        <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="delivery-frequency">
          How often is the {{ requestNoun() }} made?
          @if (store.deliveryFrequencyLocked()) {
            <i class="material-icons text-mint-dark" style="font-size: 1.4rem; cursor: help"
              [title]="lockedFrequencyReason()">info</i>
          }
        </label>
        <!-- Also placeholder-free: One Time is pre-selected on load, matching V1's default, so
             the common case needs no interaction and no start date.

             /!\ LOCKED ON A CLOSE, not hidden -- it was hidden for about an hour (AndyP,
             2026-09-23). The frequency is filed either way and the trade team reads it, so a
             control that disappears turns a stated answer into an invisible one. Greyed out on
             One Time says the same thing in the place the reader looks for it.

             The store rewrites the control when Close Account is ticked, so the value shown here
             IS the value submitted -- the `[selected]` binding needs no special case. A native
             `disabled` for the same reason the method select uses one: it leaves the form value
             intact and submittable, where pointer-events/opacity only looks disabled. -->
        <select id="delivery-frequency" class="select w-320"
          [disabled]="store.deliveryFrequencyLocked()"
          (change)="onFrequency($event)">
          @for (frequency of store.deliveryFrequencies(); track frequency.id) {
            <option [value]="frequency.id" [selected]="frequency.id === store.selectedDeliveryFrequency()?.id">
              {{ frequency.name }}
            </option>
          }
        </select>
        @if (store.deliveryFrequencyLocked()) {
          <span class="text-xs text-gray">{{ lockedFrequencyReason() }}</span>
        }
      </div>

      <!-- Shown only for a repeating schedule: a one-time transfer has nothing to begin. Keyed on
           the frequency's own flag rather than on its name or id.
           Never on a close, which is locked to One Time -- see `startDateRequired`. -->
      @if (store.startDateRequired()) {
        <div class="flex flex-col gap-y-5">
          <!-- Same treatment as the amount and the bank account: this is required whenever it is
               rendered at all, and an empty datepicker looks no different from an optional one. -->
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="start-date">
            When should the {{ requestNoun() }} initiate?
            @if (store.startDateMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <!-- The shared `.datepicker-input-container` recipe (styles.input.scss:377): a relative
               container, an input with right padding, and an ABSOLUTELY positioned icon sitting in
               that padding. Same markup shape as V1's delivery step, so the two look identical.

               ⚠️ Was a `<mat-datepicker-toggle matIconSuffix>`, which is why the icon hung outside
               the box: `matIconSuffix` only does anything inside a `<mat-form-field>`, and there
               is none here, so the attribute was inert and the toggle laid out as a block element
               AFTER the input instead of within it.

               A ligature icon rather than V1's `<mat-icon svgIcon="calendar">`: it needs neither
               MatIconModule nor an entry in the SVG registry, and every other icon in this wizard
               is a ligature. `.datepicker-icon` already supplies the positioning and `mi-18`. -->
          <div class="datepicker-input-container w-320">
            <input id="start-date" class="datepicker-input" formControlName="startDate" readonly
              placeholder="mm/dd/yyyy"
              [class.border-red-dark]="store.startDateMissing()"
              [attr.aria-invalid]="store.startDateMissing() ? 'true' : null"
              [matDatepicker]="picker" [min]="store.minStartDate()" (click)="picker.open()" />
            <!-- `pointer-events: none` so a click on the icon falls through to the input, which is
                 what opens the picker. Without it the icon is a dead patch in the middle of a
                 control that is otherwise clickable everywhere -- the one papercut V1 has here. -->
            <i class="material-icons datepicker-icon" style="pointer-events: none"
              aria-hidden="true">calendar_today</i>
            <mat-datepicker #picker color="accent" />
          </div>
        </div>
      }
    </section>

  </div>

  <!-- ⚠️ No blocked-reason message here on purpose. It is rendered once, beside Next, by the
       wizard shell (`reallocation-v2-request.component.html`) from this same computed. Showing it
       in both places put the identical sentence twice on one screen, which reads as two problems
       and makes the advisor look for a second thing to fix.

       Beside Next is the right of the two homes: that is the control it disables, and this step's
       two columns have no single place a step-level message belongs to -- under the left column it
       looked like a complaint about the amount even when the delivery details were at fault. -->
</div>
